import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PageUrls } from '@constants/page-urls.constants';
import { ActivityDataDeleteResult, ActivityDataToDelete } from '@models/activity.model';
import { ShoppingCartData } from '@models/shopping-cart.model';
import { BrandingInfo, LoginInfo, LogoutInfo, UserProfile } from '@models/user-profile.model';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { AuthenticationService } from '@services/authentication/authentication.service';
import {
  LocalStorageService,
  SessionStorageService
} from '@services/browser-storage/browser-storage.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { Observable, catchError, forkJoin, map, mergeMap, of, throwError } from 'rxjs';
import { ViewService } from 'tangoe-design-system';

@Injectable({
  providedIn: 'root'
})
export class OrchestratorService {
  constructor(
    private activity_manager_svc: ActivityManagerService,
    private auth_svc: AuthenticationService,
    private local_storage_svc: LocalStorageService,
    private session_storage_svc: SessionStorageService,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private view_svc: ViewService
  ) {}

  initialize(): void {
    this.session_storage_svc.setContext(this.session_svc);
    this.local_storage_svc.setContext(this.session_svc);
  }

  authenticate(
    email: string,
    client_code: string,
    m_email?: string,
    m_client_code?: string
  ): Observable<boolean> {
    this.invalidate();

    return this.auth_svc.login(email, client_code, m_email, m_client_code).pipe(
      catchError(() => of(undefined)),
      map((res: LoginInfo | undefined) => {
        this.session_svc.setSessionId(res?.session_id);
        return Boolean(res);
      })
    );
  }

  initializeContext(): Observable<boolean> {
    if (this.session_svc.hasUser()) return of(true);

    return this.auth_svc.getUserData('global').pipe(
      catchError((error: any) =>
        error instanceof HttpErrorResponse && error.status < 500
          ? of(undefined)
          : throwError(() => error)
      ),
      mergeMap((user_profile: UserProfile | undefined) => {
        if (!user_profile) {
          this.invalidate();
          return of(false);
        }

        this.onInitializeContext(user_profile);

        return forkJoin<[boolean, boolean]>([
          this.auth_svc.getBranding(user_profile.tenant.id, 'global').pipe(
            catchError(() => of(undefined)),
            map((branding: BrandingInfo | undefined) => {
              if (branding) this.session_svc.setBranding(branding);
              return Boolean(branding);
            })
          ),
          this.shopping_svc
            .loadDefaultCartItems()
            .pipe(map((cart: ShoppingCartData | undefined) => Boolean(cart)))
        ]).pipe(map(([branding, cart]) => branding && cart));
      })
    );
  }

  invalidateContext(): Observable<boolean> {
    const data: ActivityDataToDelete = this.activity_manager_svc.state().activityData(true);
    const activity$: Observable<ActivityDataDeleteResult | undefined> =
      !data.cart && !data.orders.length
        ? of(undefined)
        : this.shopping_svc.deleteUserActivity(data);

    return activity$.pipe(
      mergeMap(() =>
        this.auth_svc.logout().pipe(
          catchError(() => of(undefined)),
          map((res: LogoutInfo | undefined) => {
            if (res) this.invalidate(res);
            return Boolean(res);
          })
        )
      )
    );
  }

  sessionExpiredGuard(router: Router, url: string): Observable<boolean> {
    if (this.session_svc.isExpired()) {
      if (!url.endsWith(PageUrls.LOGOUT)) router.navigate([PageUrls.LOGOUT]);
      return of(url.endsWith(PageUrls.LOGOUT));
    }

    return this.initializeContext().pipe(
      catchError(() => of(false)),
      map((res: boolean) => {
        if (res) router.navigate([PageUrls.HOME]);
        else {
          this.invalidate();
          if (
            !url.endsWith(PageUrls.LOGOUT) &&
            (!this.view_svc.isLocalhost() || !url.endsWith(PageUrls.LOGIN))
          ) {
            router.navigate([PageUrls.LOGOUT]);
            res = true;
          }
        }
        return !res;
      })
    );
  }

  invalidate(res: LogoutInfo | true | undefined = undefined): boolean {
    this.session_storage_svc.clearContextItems();
    this.activity_manager_svc.reset();
    this.session_svc.invalidate(res);

    return this.session_svc.wasInitialized();
  }

  private onInitializeContext(user_profile: UserProfile): void {
    this.session_svc.initialize(user_profile);
    this.activity_manager_svc.state().initialize();
  }
}
